import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Translate as TranslateIcon,
  Groups as CultureIcon
} from '@mui/icons-material';

interface TailoredDocument {
  id: number;
  name: string;
  student_id: number;
  student_name: string;
  student_grade_level?: string;
  adjustment_type?: string;
  original_reading_level?: number;
  target_reading_level?: number;
  original_grade_level?: string;
  target_grade_level?: string;
  original_language?: string;
  target_language?: string;
  cultures_applied?: string[];
  tailoring_summary: string;
  tailoring_notes?: string;
  created_at: string;
  file_size?: number;
  output_format?: string;
}

interface Document {
  id: number;
  name: string;
  reading_level?: number;
  language: string;
}

interface TailoredVersionsDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  onDelete?: () => void;
}

const TailoredVersionsDialog: React.FC<TailoredVersionsDialogProps> = ({
  open,
  onClose,
  document,
  onDelete
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();
  
  const [tailoredDocs, setTailoredDocs] = useState<TailoredDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<{[key: number]: string | null}>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<{[key: number]: boolean}>({});
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [interactivePreviewOpen, setInteractivePreviewOpen] = useState(false);
  const [interactivePreviewPages, setInteractivePreviewPages] = useState<any[]>([]);
  const [interactivePreviewName, setInteractivePreviewName] = useState('');

  // Fetch tailored documents when dialog opens
  useEffect(() => {
    if (open && document && token) {
      fetchTailoredDocuments();
    }
  }, [open, document, token]);

  // Fetch thumbnails when tailored docs load
  useEffect(() => {
    tailoredDocs.forEach((doc) => {
      if (thumbnails[doc.id] === undefined) {
        fetchThumbnail(doc.id);
      }
    });
  }, [tailoredDocs]);

  const fetchTailoredDocuments = async () => {
    if (!document || !token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/documents/${document.id}/tailored`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch tailored documents');
      
      const data = await response.json();
      if (data.success) {
        setTailoredDocs(data.tailored_documents);
      } else {
        throw new Error(data.error || 'Failed to fetch tailored documents');
      }
    } catch (err) {
      console.error('Error fetching tailored documents:', err);
      setError(t('tailoredVersions.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchThumbnail = async (tailoredDocId: number) => {
    if (!token || loadingThumbnails[tailoredDocId]) return;
    
    setLoadingThumbnails(prev => ({ ...prev, [tailoredDocId]: true }));
    
    try {
      const response = await fetch(`/api/tailored-documents/${tailoredDocId}/thumbnail`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch thumbnail');
      
      const data = await response.json();
      if (data.success && data.thumbnail) {
        setThumbnails(prev => ({ ...prev, [tailoredDocId]: data.thumbnail }));
      } else {
        setThumbnails(prev => ({ ...prev, [tailoredDocId]: null }));
      }
    } catch (error) {
      console.error('Error fetching thumbnail:', error);
      setThumbnails(prev => ({ ...prev, [tailoredDocId]: null }));
    } finally {
      setLoadingThumbnails(prev => ({ ...prev, [tailoredDocId]: false }));
    }
  };

  const handlePreview = async (tailoredDocId: number, docName?: string) => {
    if (!token) return;
    
    try {
      const response = await fetch(`/api/tailored-documents/${tailoredDocId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to get preview URL');
      
      const data = await response.json();
      if (data.success && data.output_format === 'interactive' && data.pages) {
        setInteractivePreviewPages(data.pages);
        setInteractivePreviewName(data.document_name || docName || '');
        setInteractivePreviewOpen(true);
      } else if (data.success && data.download_url) {
        window.open(data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error previewing tailored document:', error);
    }
  };

  const handleDelete = async (tailoredDoc: TailoredDocument) => {
    if (!token || !document) return;
    
    setDeletingId(tailoredDoc.id);
    
    try {
      const response = await fetch(`/api/documents/${document.id}/tailored/${tailoredDoc.student_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to delete tailored document');
      
      const data = await response.json();
      if (data.success) {
        // Remove from local state
        setTailoredDocs(prev => prev.filter(d => d.id !== tailoredDoc.id));
        // Remove thumbnail
        setThumbnails(prev => {
          const newThumbnails = { ...prev };
          delete newThumbnails[tailoredDoc.id];
          return newThumbnails;
        });
        // Notify parent to refresh if needed
        if (onDelete) onDelete();
      }
    } catch (error) {
      console.error('Error deleting tailored document:', error);
      setError(t('tailoredVersions.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {
    setTailoredDocs([]);
    setThumbnails({});
    setLoadingThumbnails({});
    setError(null);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLanguageName = (code?: string) => {
    if (!code) return '';
    const names: {[key: string]: string} = {
      'en': 'English', 'es': 'Spanish', 'fr': 'French', 'de': 'German',
      'it': 'Italian', 'pt': 'Portuguese', 'zh': 'Chinese', 'ja': 'Japanese', 'ko': 'Korean'
    };
    return names[code] || code.toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="tailored-versions-dialog-title"
    >
      <DialogTitle id="tailored-versions-dialog-title" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Box>
          <Typography variant="h6" component="span">
            {t('tailoredVersions.title')}
          </Typography>
          {document && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {document.name}
            </Typography>
          )}
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          aria-label={t('common.close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              {t('common.loading')}
            </Typography>
          </Box>
        ) : tailoredDocs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <SchoolIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('tailoredVersions.noVersions')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('tailoredVersions.noVersionsDescription')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {tailoredDocs.map((doc, index) => (
              <React.Fragment key={doc.id}>
                {index > 0 && <Divider />}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  p: 2.5,
                  '&:hover': { bgcolor: '#f9f9f9' }
                }}>
                  {/* Thumbnail */}
                  <Tooltip title={t('tailoredVersions.clickToView')}>
                    <Box
                      onClick={() => handlePreview(doc.id, doc.name)}
                      sx={{
                        width: 80,
                        height: 110,
                        border: '2px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f5f5f5',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                          borderColor: '#1976d2',
                          transform: 'scale(1.03)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        '&:hover .thumbnail-overlay': {
                          opacity: 1,
                        }
                      }}
                      aria-label={t('tailoredVersions.previewFor', { name: doc.student_name })}
                    >
                      {loadingThumbnails[doc.id] ? (
                        <CircularProgress size={24} />
                      ) : thumbnails[doc.id] ? (
                        <>
                          <img
                            src={thumbnails[doc.id]!}
                            alt={t('tailoredVersions.thumbnailAlt', { name: doc.student_name })}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: 'top'
                            }}
                          />
                          <Box
                            className="thumbnail-overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.2s ease'
                            }}
                          >
                            <VisibilityIcon sx={{ color: '#fff' }} />
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <VisibilityIcon sx={{ color: '#999', fontSize: '1.5rem' }} />
                          <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: '0.65rem' }}>
                            {t('tailoredVersions.view')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                  
                  {/* Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Student Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon sx={{ fontSize: '1.1rem', color: '#666' }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {doc.student_name}
                      </Typography>
                      {doc.student_grade_level && (
                        <Chip 
                          label={doc.student_grade_level} 
                          size="small" 
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                    
                    {/* Tailoring Details */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                      {/* Grade Level Change */}
                      {doc.adjustment_type === 'grade_level' && doc.target_grade_level && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: '#e8f5e9',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.85rem'
                        }}>
                          <SchoolIcon sx={{ fontSize: '1rem', color: '#2e7d32' }} />
                          <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                            {t('tailoredVersions.gradeLevel')}: {doc.original_grade_level || '?'}
                          </Typography>
                          <ArrowIcon sx={{ fontSize: '0.9rem', color: '#2e7d32' }} />
                          <Typography variant="body2" sx={{ color: '#2e7d32' }} fontWeight={600}>
                            {doc.target_grade_level}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Reading Level Change */}
                      {doc.adjustment_type !== 'grade_level' && 
                       doc.original_reading_level && doc.target_reading_level && 
                       doc.original_reading_level !== doc.target_reading_level && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: '#e3f2fd',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.85rem'
                        }}>
                          <SchoolIcon sx={{ fontSize: '1rem', color: '#1976d2' }} />
                          <Typography variant="body2" color="primary">
                            {t('tailoredVersions.readingLevel')}: {doc.original_reading_level}
                          </Typography>
                          <ArrowIcon sx={{ fontSize: '0.9rem', color: '#1976d2' }} />
                          <Typography variant="body2" color="primary" fontWeight={600}>
                            {doc.target_reading_level}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Language Change */}
                      {doc.original_language && doc.target_language && 
                       doc.original_language !== doc.target_language && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: '#fff3e0',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.85rem'
                        }}>
                          <TranslateIcon sx={{ fontSize: '1rem', color: '#e65100' }} />
                          <Typography variant="body2" sx={{ color: '#e65100' }}>
                            {getLanguageName(doc.original_language)}
                          </Typography>
                          <ArrowIcon sx={{ fontSize: '0.9rem', color: '#e65100' }} />
                          <Typography variant="body2" sx={{ color: '#e65100' }} fontWeight={600}>
                            {getLanguageName(doc.target_language)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Cultures */}
                      {doc.cultures_applied && doc.cultures_applied.length > 0 && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: '#f3e5f5',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.85rem'
                        }}>
                          <CultureIcon sx={{ fontSize: '1rem', color: '#7b1fa2' }} />
                          <Typography variant="body2" sx={{ color: '#7b1fa2' }}>
                            {doc.cultures_applied.join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Tailoring Notes */}
                    {doc.tailoring_notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontStyle: 'italic',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        "{doc.tailoring_notes}"
                      </Typography>
                    )}
                    
                    {/* Date */}
                    <Typography variant="caption" color="text.secondary">
                      {t('tailoredVersions.createdAt')}: {formatDate(doc.created_at)}
                    </Typography>
                  </Box>
                  
                  {/* Actions */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                    <Tooltip title={t('tailoredVersions.viewDocument')}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreview(doc.id, doc.name)}
                        aria-label={t('tailoredVersions.viewFor', { name: doc.student_name })}
                      >
                        {t('tailoredVersions.view')}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t('tailoredVersions.deleteDocument')}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={deletingId === doc.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.id}
                        aria-label={t('tailoredVersions.deleteFor', { name: doc.student_name })}
                      >
                        {t('common.delete')}
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {tailoredDocs.length > 0 && (
              t('tailoredVersions.count', { count: tailoredDocs.length })
            )}
          </Typography>
          <Button onClick={handleClose}>
            {t('common.close')}
          </Button>
        </Box>
      </DialogActions>

      {/* Interactive Document Preview Dialog */}
      <Dialog
        open={interactivePreviewOpen}
        onClose={() => setInteractivePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        aria-label={t('assignmentDialog.interactivePreview', 'Interactive Document Preview')}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span" noWrap sx={{ flex: 1, mr: 1 }}>
            {interactivePreviewName}
          </Typography>
          <Chip label={t('assignmentDialog.interactiveLabel', 'Interactive / Fillable')} size="small" color="info" sx={{ mr: 1, flexShrink: 0 }} />
          <IconButton onClick={() => setInteractivePreviewOpen(false)} aria-label={t('common.close', 'Close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#e0e0e0' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, alignItems: 'center' }}>
            {interactivePreviewPages.map((page: any) => (
              <Paper
                key={page.page_number}
                elevation={3}
                sx={{ position: 'relative', width: '100%', maxWidth: 800, overflow: 'hidden' }}
              >
                {page.background_image_url ? (
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={page.background_image_url}
                      alt={`${t('assignmentDialog.pageLabel', 'Page')} ${page.page_number}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                    {(page.fields || []).map((field: any) => (
                      <Box
                        key={field.id}
                        sx={{
                          position: 'absolute',
                          left: `${field.x_pct}%`,
                          top: `${field.y_pct}%`,
                          width: `${field.width_pct}%`,
                          height: `${field.height_pct}%`,
                          border: '1.5px dashed rgba(25, 118, 210, 0.6)',
                          borderRadius: '3px',
                          bgcolor: 'rgba(25, 118, 210, 0.06)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          px: 0.5,
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(25, 118, 210, 0.8)',
                            fontSize: '0.6rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {field.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {t('assignmentDialog.pageImageUnavailable', 'Page image unavailable')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ px: 2, py: 0.5, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('assignmentDialog.pageLabel', 'Page')} {page.page_number}
                    {page.fields?.length > 0 && ` \u2022 ${page.fields.length} ${t('assignmentDialog.fieldsLabel', 'fields')}`}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setInteractivePreviewOpen(false)} variant="contained">
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TailoredVersionsDialog;

